import {
  Box,
  Divider,
  Fade,
  Flex,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React, { useEffect } from "react";
import useQuote from "../../hooks/useQuote";
import { FormSectionStatus } from "../../lib/models/form-section-status.model";
import { CalculationType } from "../../state/quote-reducer";
import RentalBorrowerInformationForm from "../rental-quote/RentalBorrowerInformationForm";
import RentalPricingForm from "../rental-quote/RentalPricingForm";
import RentalPropertySummaryForm from "../rental-quote/RentalPropertySummary";
import BorrowerInformationForm from "../stf-quote/BorrowerInformationForm";
import DealTermsForm from "../stf-quote/DealTermsForm";
import LoanSummaryForm from "../stf-quote/LoanSummaryForm";
import LoanTermsForm from "../stf-quote/LoanTermsForm";
import FormNavigation from "./FormNavigation";

interface FormTabsContainerProps {
  tabIndex: number;
  formStatusSections: FormSectionStatus[];
  calculation: CalculationType;
  overrideContent: React.ReactNode;
  calcInfo: React.ReactNode;
  discussionContent?: React.ReactNode;
  isFnf: boolean;
}

function FormTabsContainer({
  tabIndex,
  formStatusSections,
  calculation,
  overrideContent,
  calcInfo,
  discussionContent,
  isFnf,
}: FormTabsContainerProps) {
  const { state } = useQuote();
  const { validateForm, setFieldTouched } = useFormikContext();

  useEffect(() => {
    if (state.isNewQuote) return;

    validateForm().then((x) =>
      Object.keys(x).forEach((error) => {
        setFieldTouched(error);
      })
    );
  }, [setFieldTouched, state.isNewQuote, validateForm]);

  return (
    <Tabs index={tabIndex}>
      <TabPanels>
        <TabPanel>
          <Fade in={tabIndex === 0}>
            <Flex>
              <FormNavigation sections={formStatusSections} />
              <Box flex="1" paddingX={{ base: 0, md: 12, lg: 12 }}>
                {isFnf ? (
                  <>
                    <LoanSummaryForm />
                    <Divider marginY="12" />
                    <BorrowerInformationForm />
                    <Divider marginY="12" />
                    <LoanTermsForm />
                    <Divider marginY="12" />
                    <DealTermsForm />
                  </>
                ) : (
                  <>
                    <RentalBorrowerInformationForm
                      initialQuoteType="Rental"
                      quoteType={state.quoteType ?? "Rental"}
                    />
                    <Divider marginY="12" />
                    <RentalPropertySummaryForm
                      quoteType={state.quoteType ?? "Rental"}
                    />
                    <Divider marginY="12" />
                    <RentalPricingForm
                      quoteType={state.quoteType ?? "Rental"}
                    />
                  </>
                )}
              </Box>
            </Flex>
          </Fade>
        </TabPanel>

        <TabPanel>
          <Fade in={tabIndex === 1}>
            <Box>{calculation && calcInfo}</Box>
          </Fade>
        </TabPanel>

        <TabPanel>
          <Fade in={tabIndex === 2}>
            <Flex>
              <Box flex="1">{calculation && overrideContent}</Box>
            </Flex>
          </Fade>
        </TabPanel>

        <TabPanel>
          <Fade in={tabIndex === 3}>
            <Box flex="1">{calculation && discussionContent}</Box>
          </Fade>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}

export default FormTabsContainer;
